import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { invalidateFollowUpActionQueries } from "@/lib/follow-up-actions/invalidate-follow-up-action-queries";
import { offlineDb, type SyncQueueItem } from "@/lib/offline/db";
import { MAX_RETRIES, syncService } from "@/lib/offline/syncService";

/**
 * Queue item types that change follow-up-action data once they reach the server.
 * Typed against the queue's own union so renaming a type there fails the build
 * rather than silently leaving this hook matching nothing.
 *
 * Photos are counted as well as the finalize: `syncService` throws a finalize
 * back onto the queue while any photo for the same action is still pending, so
 * counting the finalize alone would drain — and fire — before the completion had
 * actually landed.
 */
const FOLLOW_UP_ACTION_QUEUE_TYPES: SyncQueueItem["type"][] = [
  "followUpFinalize",
  "followUpPhoto",
];

/**
 * Refresh every follow-up-action surface when queued completion work finishes
 * syncing, wherever the user happens to be at the time.
 *
 * Completing an action is an offline-first write: it lands in IndexedDB and the
 * status PATCH is queued behind it, so nothing on the server has changed by the
 * time the completion screen closes. The manager is usually back on the
 * dashboard before the queue drains, and `awaitingSignOff` — the flag that
 * renders the sign-off prompt — comes from the dashboard status query, which
 * nothing else invalidates at that point. Without this hook the manager cannot
 * sign the action off until a manual refresh.
 *
 * Mount once, high in the authenticated tree.
 */
export function useFollowUpActionSyncRefresh(): void {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  const previousPendingRef = useRef(0);
  // Checks run one after another rather than concurrently — see below.
  const checkChainRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = syncService.subscribe(() => {
      // Queued one behind the last, so the counts are read in the order the
      // notifications arrived and every one of them is recorded.
      //
      // An earlier version ran them concurrently and kept only the newest,
      // discarding any result a later check had superseded. That drops the very
      // observation the transition depends on: a sync that succeeds first time
      // notifies at its start (queue holds the item) and again at its finish
      // (queue empty) a few milliseconds apart, so the opening count was always
      // superseded before it resolved. The hook then compared empty against
      // empty, saw no transition, and never refreshed.
      checkChainRef.current = checkChainRef.current
        .then(async () => {
          // Counted here rather than read off the status object, which reports
          // the whole queue: unrelated work would fire this on every drain, and
          // a single permanently-failed item of any type would hold the total
          // above zero so the transition never came again. Items that have
          // exhausted their retries are excluded for the same reason.
          const pending = await offlineDb.syncQueue
            .filter(
              (item) =>
                FOLLOW_UP_ACTION_QUEUE_TYPES.includes(item.type) &&
                item.attempts < MAX_RETRIES,
            )
            .count();

          const previous = previousPendingRef.current;
          previousPendingRef.current = pending;

          if (previous > 0 && pending === 0) {
            await invalidateFollowUpActionQueries(queryClientRef.current);
          }
        })
        .catch((err) => {
          console.error("follow-up action sync refresh check failed", err);
        });
    });

    return () => {
      unsubscribe();
    };
  }, []);
}
